/**
 * IncomingQuoteRequests.jsx
 *
 * Página para que los proveedores vean las solicitudes de cotización entrantes.
 */

import { useState, useEffect, useCallback } from "react"
import {
    Table,
    Tag,
    Button,
    Typography,
    Card,
    Empty,
    Spin,
    message,
    Tooltip,
    Layout,
    Modal,
    Descriptions,
    Divider
} from "antd"
import {
    SendOutlined,
    ReloadOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    EditOutlined,
    EyeOutlined
} from "@ant-design/icons"
import { useAuth } from "../contexts/authContext"
import {
    getIncomingQuoteRequests,
    getProviderResponseStatusInfo,
    formatPrice
} from "../services/QuoteRequestServices"
import { QuoteResponseModal } from "../components/modals/QuoteResponseModal"
import "../assets/styles/IncomingQuoteRequests.css"

const { Content } = Layout
const { Text } = Typography

const IncomingQuoteRequests = ({ embedded = false }) => {
    const [quoteRequests, setQuoteRequests] = useState([])
    const [loading, setLoading] = useState(true)
    const [selectedRequest, setSelectedRequest] = useState(null)
    const [responseModalVisible, setResponseModalVisible] = useState(false)
    const [viewQuoteModalVisible, setViewQuoteModalVisible] = useState(false)
    const [selectedQuoteToView, setSelectedQuoteToView] = useState(null)
    const { getToken, currentUser } = useAuth()

    const fetchQuoteRequests = useCallback(async () => {
        try {
            setLoading(true)
            const token = await getToken()
            const data = await getIncomingQuoteRequests(token)
            setQuoteRequests(data.data || [])
        } catch (error) {
            console.error("Error fetching incoming quote requests:", error)
            message.error("Error al cargar las solicitudes entrantes")
        } finally {
            setLoading(false)
        }
    }, [getToken])

    useEffect(() => {
        fetchQuoteRequests()
    }, [fetchQuoteRequests])

    const handleRespond = (request) => {
        setSelectedRequest(request)
        setResponseModalVisible(true)
    }

    const handleResponseSuccess = () => {
        setResponseModalVisible(false)
        setSelectedRequest(null)
        fetchQuoteRequests()
    }

    const getMyResponseStatus = (request) => {
        if (!request.responses || !currentUser?.uid) return null
        return request.responses[currentUser.uid]?.status || "pending"
    }

    const getMyResponse = (request) => {
        if (!request.responses || !currentUser?.uid) return null
        return request.responses[currentUser.uid] || null
    }

    const handleViewQuote = (request) => {
        setSelectedQuoteToView(request)
        setViewQuoteModalVisible(true)
    }

    // Renderizar detalle de productos con medidas
    const renderProductDetails = (record) => {
        // Si es solicitud de carrito, mostrar cart_products
        if (record.type === "cart_request" && record.cart_products?.length > 0) {
            return (
                <div>
                    <Text strong style={{ fontSize: 13 }}>
                        {record.cart_products.length} producto(s)
                    </Text>
                    <div style={{ marginTop: 4 }}>
                        {record.cart_products.slice(0, 2).map((prod, idx) => (
                            <div key={idx} style={{ fontSize: 12, color: "#666" }}>
                                • {prod.product_name}
                                {prod.requested_product_name &&
                                    prod.requested_product_name !== prod.product_name && (
                                        <span style={{ color: "#999" }}>
                                            {" "}Solicitado: {prod.requested_product_name}
                                        </span>
                                    )}
                                {prod.measures?.length > 0 && (
                                    <span style={{ color: "#999" }}>
                                        {" "}({prod.measures.map(m => `${m.measure}: ${m.quantity}`).join(", ")})
                                    </span>
                                )}
                            </div>
                        ))}
                        {record.cart_products.length > 2 && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                y {record.cart_products.length - 2} más...
                            </Text>
                        )}
                    </div>
                </div>
            )
        }

        // Solicitud individual
        return (
            <div>
                <Text strong>{record.original_product_name}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {record.original_product_category}
                </Text>
            </div>
        )
    }

    // Renderizar cantidad total
    const renderQuantity = (record) => {
        if (record.type === "cart_request" && record.cart_products?.length > 0) {
            const totalItems = record.cart_products.reduce((sum, prod) => {
                const prodQty = prod.measures?.reduce((s, m) => s + (m.quantity || 0), 0) || prod.quantity || 0
                return sum + prodQty
            }, 0)
            return (
                <div>
                    <Text strong>{totalItems}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 11 }}>items total</Text>
                </div>
            )
        }
        return <Text strong>{record.quantity}</Text>
    }

    const columns = [
        {
            title: "Producto(s) Solicitado(s)",
            key: "product",
            render: (_, record) => renderProductDetails(record)
        },
        {
            title: "Cantidad",
            key: "quantity",
            width: 100,
            align: "center",
            render: (_, record) => renderQuantity(record)
        },
        {
            title: "Notas",
            dataIndex: "notes",
            key: "notes",
            width: 180,
            ellipsis: true,
            render: (text, record) => text || record.specifications || <Text type="secondary">-</Text>
        },
        {
            title: "Comprador",
            key: "buyer",
            width: 150,
            render: (_, record) => (
                <div>
                    <Text>{record.buyer_name || "Comprador"}</Text>
                    {record.buyer_company && (
                        <>
                            <br />
                            <Text type="secondary" style={{ fontSize: 11 }}>{record.buyer_company}</Text>
                        </>
                    )}
                </div>
            )
        },
        {
            title: "Mi Estado",
            key: "my_status",
            width: 130,
            render: (_, record) => {
                const status = getMyResponseStatus(record)
                const statusInfo = getProviderResponseStatusInfo(status)

                let icon = <ClockCircleOutlined />
                if (status === "draft") icon = <EditOutlined />
                if (status === "responded") icon = <CheckCircleOutlined />
                if (status === "declined") icon = <CloseCircleOutlined />

                return (
                    <Tag color={statusInfo.color} icon={icon}>
                        {statusInfo.label}
                    </Tag>
                )
            }
        },
        {
            title: "Recibida",
            dataIndex: "created_at",
            key: "created_at",
            width: 120,
            render: (date) => new Date(date).toLocaleDateString("es-AR")
        },
        {
            title: "Acciones",
            key: "actions",
            width: 180,
            render: (_, record) => {
                const status = getMyResponseStatus(record)

                if (status === "responded") {
                    return (
                        <Tooltip title="Ver detalle de tu cotización">
                            <Button
                                type="default"
                                icon={<EyeOutlined />}
                                onClick={() => handleViewQuote(record)}
                            >
                                Ver Cotización
                            </Button>
                        </Tooltip>
                    )
                }

                if (status === "declined") {
                    return (
                        <Tag color="error" icon={<CloseCircleOutlined />}>
                            Rechazada
                        </Tag>
                    )
                }

                // Si es draft (borrador de email), mostrar botón especial
                if (status === "draft") {
                    return (
                        <Tooltip title="Revisa los datos extraídos de tu email y confirma la cotización">
                            <Button
                                type="primary"
                                style={{ backgroundColor: "#faad14", borderColor: "#faad14" }}
                                icon={<EditOutlined />}
                                onClick={() => handleRespond(record)}
                            >
                                Revisar y Confirmar
                            </Button>
                        </Tooltip>
                    )
                }

                return (
                    <Tooltip title="Responder con cotización">
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            onClick={() => handleRespond(record)}
                        >
                            Cotizar
                        </Button>
                    </Tooltip>
                )
            }
        }
    ]

    const content = (
        <>
            <div className={embedded ? "incoming-quotes__header-embedded" : "incoming-quotes__header"}>
                <div>
                    {!embedded && <p className="incoming-quotes__eyebrow">Panel de proveedor</p>}
                    <h1 className={embedded ? "incoming-quotes__title-embedded" : "incoming-quotes__title"}>
                        Solicitudes de Cotización
                    </h1>
                    {!embedded && (
                        <p className="incoming-quotes__subtitle">
                            Revisa las solicitudes de compradores y envía tus cotizaciones directamente desde aquí.
                        </p>
                    )}
                </div>
                <div className="incoming-quotes__actions">
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchQuoteRequests}
                        loading={loading}
                    >
                        Actualizar
                    </Button>
                </div>
            </div>

            <div className="incoming-quotes__content">
                <Card className={embedded ? "incoming-quotes__card-embedded" : "incoming-quotes__card"}>
                    {loading ? (
                        <div style={{ textAlign: "center", padding: embedded ? 32 : 64 }}>
                            <Spin size="large" />
                            <p style={{ marginTop: 16, color: "#666" }}>Cargando solicitudes...</p>
                        </div>
                    ) : quoteRequests.length === 0 ? (
                        <div className="incoming-quotes__empty">
                            <Empty
                                description="No tienes solicitudes de cotización pendientes"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            >
                                <p style={{ color: "#666", marginTop: 8 }}>
                                    Cuando los compradores soliciten cotizaciones, aparecerán aquí.
                                </p>
                            </Empty>
                        </div>
                    ) : (
                        <Table
                            className="incoming-quotes__table"
                            dataSource={quoteRequests}
                            columns={columns}
                            rowKey="id"
                            pagination={{
                                pageSize: embedded ? 5 : 10,
                                showSizeChanger: !embedded,
                                showTotal: (total) => `${total} solicitudes`
                            }}
                            size={embedded ? "small" : "middle"}
                        />
                    )}
                </Card>
            </div>
        </>
    )

    const responseModal = (
        <QuoteResponseModal
            visible={responseModalVisible}
            onCancel={() => {
                setResponseModalVisible(false)
                setSelectedRequest(null)
            }}
            quoteRequest={selectedRequest}
            getToken={getToken}
            onSuccess={handleResponseSuccess}
        />
    )

    // Modal para ver la cotización enviada
    const myResponse = selectedQuoteToView ? getMyResponse(selectedQuoteToView) : null
    const viewQuoteModal = (
        <Modal
            title="Mi Cotización Enviada"
            open={viewQuoteModalVisible}
            onCancel={() => {
                setViewQuoteModalVisible(false)
                setSelectedQuoteToView(null)
            }}
            footer={[
                <Button key="close" onClick={() => {
                    setViewQuoteModalVisible(false)
                    setSelectedQuoteToView(null)
                }}>
                    Cerrar
                </Button>
            ]}
            width={700}
        >
            {selectedQuoteToView && myResponse && (
                <div>
                    {/* Información del pedido */}
                    <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
                        <Descriptions.Item label="Comprador">
                            {selectedQuoteToView.buyer_name || "Comprador"}
                            {selectedQuoteToView.buyer_company && ` (${selectedQuoteToView.buyer_company})`}
                        </Descriptions.Item>
                        <Descriptions.Item label="Fecha de solicitud">
                            {new Date(selectedQuoteToView.created_at).toLocaleDateString("es-AR")}
                        </Descriptions.Item>
                    </Descriptions>

                    <Divider>Mi Cotización</Divider>

                    {/* Resumen de la cotización */}
                    <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
                        <Descriptions.Item label="Precio Total">
                            <Text strong style={{ color: "#059669", fontSize: 16 }}>
                                {formatPrice(myResponse.price, myResponse.currency)}
                            </Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Días de entrega">
                            {myResponse.delivery_days} días
                        </Descriptions.Item>
                        <Descriptions.Item label="Fecha de envío" span={2}>
                            {myResponse.responded_at
                                ? new Date(myResponse.responded_at).toLocaleString("es-AR")
                                : "-"
                            }
                        </Descriptions.Item>
                        {myResponse.notes && (
                            <Descriptions.Item label="Notas" span={2}>
                                {myResponse.notes}
                            </Descriptions.Item>
                        )}
                    </Descriptions>

                    {/* Detalle por producto */}
                    {myResponse.product_quotes && myResponse.product_quotes.length > 0 && (
                        <>
                            <Divider>Detalle por Producto</Divider>
                            {myResponse.product_quotes.map((quote, index) => (
                                <Card
                                    key={index}
                                    size="small"
                                    style={{ marginBottom: 12 }}
                                    title={
                                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                                            <span>{quote.product_name || `Producto ${index + 1}`}</span>
                                            <Text strong style={{ color: "#059669" }}>
                                                {formatPrice(quote.price, quote.currency || myResponse.currency)}
                                            </Text>
                                        </div>
                                    }
                                >
                                    {quote.measure_quotes && quote.measure_quotes.length > 0 ? (
                                        <table style={{ width: "100%", fontSize: 13 }}>
                                            <thead>
                                                <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                                                    <th style={{ textAlign: "left", padding: "8px 4px" }}>Medida</th>
                                                    <th style={{ textAlign: "right", padding: "8px 4px" }}>P. Unitario</th>
                                                    <th style={{ textAlign: "center", padding: "8px 4px" }}>Cantidad</th>
                                                    <th style={{ textAlign: "right", padding: "8px 4px" }}>Subtotal</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {quote.measure_quotes.map((mq, mIndex) => (
                                                    <tr key={mIndex} style={{ borderBottom: "1px solid #f5f5f5" }}>
                                                        <td style={{ padding: "8px 4px" }}>{mq.measure}</td>
                                                        <td style={{ textAlign: "right", padding: "8px 4px" }}>
                                                            {formatPrice(mq.unit_price, quote.currency || myResponse.currency)}
                                                        </td>
                                                        <td style={{ textAlign: "center", padding: "8px 4px" }}>{mq.quantity}</td>
                                                        <td style={{ textAlign: "right", padding: "8px 4px" }}>
                                                            {formatPrice(mq.subtotal, quote.currency || myResponse.currency)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : quote.unit_price ? (
                                        <div>
                                            <Text type="secondary">Precio unitario: </Text>
                                            <Text>{formatPrice(quote.unit_price, quote.currency || myResponse.currency)}</Text>
                                            <Text type="secondary"> × {quote.quantity} uds</Text>
                                        </div>
                                    ) : null}
                                </Card>
                            ))}
                        </>
                    )}
                </div>
            )}
        </Modal>
    )

    if (embedded) {
        return (
            <>
                <div className="incoming-quotes__embedded">{content}</div>
                {responseModal}
                {viewQuoteModal}
            </>
        )
    }

    return (
        <Layout className="incoming-quotes__page">
            <Content className="incoming-quotes__layout-inner">
                {content}
            </Content>
            {responseModal}
            {viewQuoteModal}
        </Layout>
    )

}

export default IncomingQuoteRequests
